/**
 * Cart/checkout side-state (zustand) — OWNED by the cart/status workstream.
 * Deliberately separate from store.ts (which another workstream owns): this
 * store only READS the main store via useStore.getState() and never patches it
 * beyond the public actions (setStep).
 *
 * Owns:
 *  - the checkout pipeline: client low-res pre-check → POST /checkout →
 *    Shopify cart handoff (real) or the demo redirect (mock),
 *  - checkout error surfaces (blocked prints banner, catalog/generic notices),
 *  - save-for-later state (saved email + resume URL, lazily hydrated from the
 *    project so a resumed order remembers it was saved).
 */

import { create } from 'zustand';
import { track } from '../lib/analytics';
import { api, ApiError } from '../lib/api';
import { CartHandoffError, runCartHandoff } from '../lib/cartHandoff';
import type { CartItem } from '../lib/types';
import { delay } from '../lib/util';
import { lastSaveFailure, useStore } from './store';

export type CheckoutPhase = 'idle' | 'submitting' | 'handoff' | 'redirecting' | 'error';

/** Which surface offered the save. Rides pp_order_saved into GA4/Clarity. */
export type SaveSource = 'upload_prompt' | 'step2' | 'cart';

export interface CheckoutNotice {
  kind: 'catalog' | 'generic' | 'handoff';
  message: string;
}

/** Demo status route the mock-mode handoff lands on (order 1482, demo token). */
export const MOCK_STATUS_PATH = '/status/1482?t=demo';

export interface CartExtrasState {
  // ---- checkout pipeline ----
  phase: CheckoutPhase;
  /** Items returned by POST /checkout, awaiting the Shopify cart handoff. */
  checkoutItems: CartItem[] | null;
  /** UiPrint ids blocked as low-res (client pre-check or server 422 detail). */
  blockedPrintIds: string[] | null;
  notice: CheckoutNotice | null;

  // ---- save for later ----
  savedEmail: string | null;
  resumeUrl: string | null;
  saveHydrated: boolean;
  saving: boolean;
  /**
   * The post-upload save prompt has had its one turn (owner 2026-07-29).
   * The batch-done modal fires on EVERY completed batch, so without this a
   * customer uploading three lots is asked three times, which is how a good
   * prompt becomes an annoying one. Store-held rather than local because
   * UploadScreen unmounts on the way to Step 2 and back.
   */
  savePromptSeen: boolean;
  /** The pre-checkout turnaround dialog has shown this session (owner
   *  2026-07-31: once per session — a repeat interstitial is a nag). */
  turnaroundSeen: boolean;

  // ---- actions ----
  /** "Checkout →": pre-check low-res, POST /checkout, then advance to step 4. */
  startCheckout: () => Promise<void>;
  /**
   * Run the post-/checkout handoff (called by the Checkout interstitial).
   * Mock mode: fake a short process then `onMockDone()` (router navigation).
   * Real mode: the /cart.js dance ending in window.location = '/checkout'.
   * Idempotent — repeat calls while already redirecting are no-ops.
   */
  runHandoff: (opts: { onMockDone: () => void }) => Promise<void>;
  /** Retry a failed handoff (phase 'error' → 'handoff'). */
  retryHandoff: () => void;
  /** Abandon the checkout attempt and return to the cart step. */
  cancelCheckout: () => void;
  dismissBlocked: () => void;
  dismissNotice: () => void;
  /**
   * Back button from Shopify's checkout: the browser restores this page from
   * its back/forward cache with the JS state frozen mid-redirect, so the
   * "Redirecting to secure checkout" spinner would spin forever. A checkout
   * still in flight is cancelled and the customer lands on the cart. No-op
   * otherwise (an error screen keeps its own Try again / Back buttons).
   */
  resetAfterPageRestore: () => void;

  /** Fetch the project's stored email once (resume-after-save support). */
  hydrateSavedEmail: () => Promise<void>;
  /**
   * POST /save — stores email, returns the resume URL. Throws ApiError.
   * `source` rides the pp_order_saved event: three surfaces offer this now and
   * without it they are indistinguishable in GA4, which makes it impossible to
   * tell whether the new post-upload prompt is the thing working.
   */
  saveOrder: (email: string, source?: SaveSource) => Promise<{ resumeUrl: string }>;
  /** Mark the post-upload prompt as spent (saved, skipped or continued past). */
  markSavePromptSeen: () => void;
  markTurnaroundSeen: () => void;
}

export const useCartExtras = create<CartExtrasState>((set, get) => ({
  phase: 'idle',
  checkoutItems: null,
  blockedPrintIds: null,
  notice: null,

  savedEmail: null,
  resumeUrl: null,
  saveHydrated: false,
  saving: false,
  savePromptSeen: false,
  turnaroundSeen: false,

  async startCheckout() {
    if (get().phase === 'submitting' || get().phase === 'handoff') return;
    const { project, prints, setStep } = useStore.getState();
    if (!project || prints.length === 0) return;

    // Client-side pre-check: unresolved low-res prints block checkout with the
    // same banner the server 422 produces (works in mock mode too).
    const blocked = prints
      .filter((p) => p.printCheck === 'lowres' && !p.overrideLowRes)
      .map((p) => p.id);
    if (blocked.length > 0) {
      set({ blockedPrintIds: blocked, notice: null });
      track('checkout_error', { reason: 'lowres' });
      return;
    }

    set({ phase: 'submitting', notice: null, blockedPrintIds: null });
    // Save FIRST. POST /checkout builds the Shopify cart from the SERVER's
    // prints, and the autosave is debounced 2 s: a +qty or an accepted
    // small-prints sheet just before this tap never reached the cart, and an
    // order that had never saved failed with "Project has no prints".
    const saved = await useStore.getState().flushNow();
    if (!saved) {
      // A 4xx is the server refusing the order as it stands, so retrying can't
      // help: say so, and log the code so we can see which rule refused it.
      const failure = lastSaveFailure();
      const refused = !!failure && failure.status >= 400 && failure.status < 500;
      set({
        phase: 'idle',
        notice: {
          kind: 'generic',
          message: refused
            ? "Some of your latest changes couldn't be saved. Refresh the page to carry on from your last saved order."
            : "We couldn't start the checkout. Check your connection and try again.",
        },
      });
      track('checkout_error', {
        reason: 'save',
        code: failure?.code ?? 'unknown',
        status: failure?.status ?? 0,
      });
      return;
    }
    try {
      const res = await api.checkout(project.id, project.token, {
        urgent: useStore.getState().urgent,
      });
      set({ phase: 'handoff', checkoutItems: res.cart.items });
      track('checkout_started', { prints: prints.length });
      setStep(3); // Checkout interstitial (step 4 of the stepper) runs the handoff
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'lowres_unresolved') {
          const detail = (err.detail ?? {}) as { print_ids?: string[] };
          const serverIds = new Set(detail.print_ids ?? []);
          // Map server print ids back to UiPrint rows (fall back to any
          // client-side low-res rows if the ids don't resolve).
          const byServerId = useStore
            .getState()
            .prints.filter((p) => p.printId && serverIds.has(p.printId))
            .map((p) => p.id);
          const fallback = useStore
            .getState()
            .prints.filter((p) => p.printCheck === 'lowres' && !p.overrideLowRes)
            .map((p) => p.id);
          set({
            phase: 'idle',
            blockedPrintIds: byServerId.length > 0 ? byServerId : fallback,
          });
          track('checkout_error', { reason: 'lowres' });
          return;
        }
        if (err.code === 'catalog_not_synced' || err.httpStatus === 503) {
          set({
            phase: 'idle',
            notice: {
              kind: 'catalog',
              message:
                'The print portal is being updated right now. Give it a minute and try again.',
            },
          });
          track('checkout_error', { reason: 'catalog' });
          return;
        }
        set({
          phase: 'idle',
          notice: {
            kind: 'generic',
            message: err.message || "We couldn't start the checkout. Please try again.",
          },
        });
        track('checkout_error', { reason: 'generic' });
        return;
      }
      set({
        phase: 'idle',
        notice: {
          kind: 'generic',
          message: "We couldn't start the checkout. Check your connection and try again.",
        },
      });
      track('checkout_error', { reason: 'network' });
    }
  },

  async runHandoff({ onMockDone }) {
    const { phase, checkoutItems } = get();
    if (phase !== 'handoff' || !checkoutItems) return;
    set({ phase: 'redirecting' });

    if (api.isMock) {
      await delay(1500); // fake the cart dance so the interstitial is visible
      onMockDone(); // route to the demo status page first (unmounts the flow)…
      // …then reset so browser-back from the status page lands on the cart.
      set({ phase: 'idle', checkoutItems: null });
      useStore.getState().setStep(2);
      return;
    }

    try {
      // Fire BEFORE the handoff — it ends in window.location = '/checkout'
      // (page unload), so anything after never runs.
      track('cart_handoff');
      // ends in window.location = '/checkout'
      await runCartHandoff(checkoutItems, {}, useStore.getState().orderNote.trim());
    } catch (e) {
      // Log the actual failing step + the variant ids so a dead/stale variant is
      // diagnosable (the shopper still sees one generic message). 'add_items'
      // failing with an otherwise-valid cart points at a variant Shopify won't
      // add — usually a stale variant_map row on the Worker side.
      const step = e instanceof CartHandoffError ? e.step : 'unknown';
      console.error(
        `[checkout] cart handoff failed at step "${step}"`,
        e,
        'variants:',
        (checkoutItems ?? []).map((i) => i.id),
      );
      set({
        phase: 'error',
        notice: {
          kind: 'handoff',
          message:
            "Your order was saved but we couldn't open the store checkout. Try again. Nothing has been charged.",
        },
      });
      track('checkout_error', { reason: 'handoff', step });
    }
  },

  retryHandoff() {
    if (get().phase === 'error' && get().checkoutItems) {
      set({ phase: 'handoff', notice: null });
    }
  },

  cancelCheckout() {
    set({ phase: 'idle', checkoutItems: null, notice: null });
    useStore.getState().setStep(2);
  },

  dismissBlocked() {
    set({ blockedPrintIds: null });
  },
  dismissNotice() {
    set({ notice: null });
  },

  resetAfterPageRestore() {
    const { phase } = get();
    if (phase !== 'submitting' && phase !== 'handoff' && phase !== 'redirecting') return;
    get().cancelCheckout(); // phase idle, items cleared, back to the cart (step 2)
    // Then reload, never resume in place: a customer who PAID and pressed Back
    // would otherwise get the paid cart again and could pay twice. The boot
    // starts a fresh order for an 'ordered' project and resumes an unpaid one.
    pageRestore.reload();
  },

  async hydrateSavedEmail() {
    if (get().saveHydrated) return;
    set({ saveHydrated: true });
    const { project } = useStore.getState();
    if (!project) return;
    try {
      const proj = await api.getProject(project.id, project.token);
      if (proj.email) set({ savedEmail: proj.email });
    } catch {
      // non-fatal — the save modal simply starts empty
    }
  },

  markSavePromptSeen() {
    set({ savePromptSeen: true });
  },

  markTurnaroundSeen() {
    set({ turnaroundSeen: true });
  },

  async saveOrder(email: string, source: SaveSource = 'cart') {
    const { project } = useStore.getState();
    if (!project) throw new ApiError('no_project', 'No active order to save.', 400);
    set({ saving: true });
    try {
      // Push a pending autosave first so the resume link restores the latest
      // cart. Best effort: the email is still worth saving if this fails.
      await useStore.getState().flushNow();
      const res = await api.saveProject(project.id, project.token, email);
      set({ savedEmail: email, resumeUrl: res.resume_url });
      track('order_saved', { source });
      return { resumeUrl: res.resume_url };
    } finally {
      set({ saving: false });
    }
  },
}));

/** Swappable in tests (jsdom can't reload). */
export const pageRestore = { reload: () => window.location.reload() };

/** pageshow handler: only a back/forward-cache RESTORE (persisted) resets;
 *  a normal load or reload is ignored (it boots fresh and CheckoutScreen
 *  already bounces an idle step 4 back to the cart). */
export function onPageShow(e: { persisted: boolean }): void {
  if (e.persisted) useCartExtras.getState().resetAfterPageRestore();
}

if (typeof window !== 'undefined') window.addEventListener('pageshow', onPageShow);
